import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FiChevronLeft, FiChevronRight, FiClock, FiUser } from 'react-icons/fi';
import { Badge, Card } from './ui';

const TIME_SLOTS = [
    '09:00', '10:00', '11:00', '12:00',
    '13:00', '14:00', '15:00', '16:00', '17:00'
];

function getMondayOfDate(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    const day = x.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    x.setDate(x.getDate() + diff);
    return x;
}

/** Local calendar YYYY-MM-DD (avoid toISOString UTC shifting the day for Asia/HK etc.) */
function formatDateForAPI(date) {
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function slotKeyFromParts(dateStr, time) {
    return `${dateStr}|${time}`;
}

const TimeSlotCalendar = ({
    itemId,
    equipmentId,
    resourceName,
    equipmentName,
    resourceType = 'equipment',
    equipmentType = 'single',
    totalUnits = 1,
    onSlotSelect,
    selectedDate,
    selectedTime,
    selectedDuration,
    isAdminMode = false,
    multiSelect = true,
    selectedSlotKeys = [],
    onSelectedSlotKeysChange,
    maxBookableDate = null,
    showStatsBar = false,
    /** Equipment only: all unit IDs in the group — availability is merged (slot free if any unit is free). */
    equipmentIdsForAvailability = null,
    /** Smaller cells and no horizontal scroll inside the parent column */
    compact = true
}) => {
    const resolvedId = itemId || equipmentId;
    const resolvedName = resourceName || equipmentName || 'Resource';
    const blockWeekends = resourceType === 'equipment';

    const equipmentIdsKey =
        Array.isArray(equipmentIdsForAvailability) && equipmentIdsForAvailability.length > 0
            ? [...new Set(equipmentIdsForAvailability.map((id) => String(id)).filter(Boolean))]
                  .sort()
                  .join('|')
            : '';

    const idsToQuery = useMemo(() => {
        if (resourceType === 'venue') {
            return resolvedId ? [String(resolvedId)] : [];
        }
        if (equipmentIdsKey) {
            return equipmentIdsKey.split('|').filter(Boolean);
        }
        return resolvedId ? [String(resolvedId)] : [];
    }, [resourceType, equipmentIdsKey, resolvedId]);

    const [currentWeekStart, setCurrentWeekStart] = useState(null);
    const [availability, setAvailability] = useState({});
    const [bookingDetails, setBookingDetails] = useState({});
    const [loading, setLoading] = useState(false);
    const [availabilityHint, setAvailabilityHint] = useState('');
    const [hoveredSlot, setHoveredSlot] = useState(null);
    const [selectedSlotForDetails, setSelectedSlotForDetails] = useState(null);

    const useMultiSelect = multiSelect && !isAdminMode && typeof onSelectedSlotKeysChange === 'function';

    /** minmax(0,1fr) lets day columns shrink so the grid fits a narrow middle column (no horizontal scroll). */
    const weekGridTemplate = useMemo(
        () =>
            compact
                ? { gridTemplateColumns: 'minmax(2rem, 2.5rem) repeat(7, minmax(0, 1fr))' }
                : { gridTemplateColumns: 'minmax(3.5rem, 5rem) repeat(7, minmax(0, 1fr))' },
        [compact]
    );

    const furthestWeekMonday = useMemo(() => {
        if (!maxBookableDate) return null;
        return getMondayOfDate(maxBookableDate);
    }, [maxBookableDate]);

    useEffect(() => {
        const today = new Date();
        const monday = getMondayOfDate(today);
        if (furthestWeekMonday && monday.getTime() > furthestWeekMonday.getTime()) {
            setCurrentWeekStart(new Date(furthestWeekMonday));
        } else {
            setCurrentWeekStart(monday);
        }
    }, [furthestWeekMonday]);

    const getWeekDates = useCallback(() => {
        if (!currentWeekStart) return [];
        const dates = [];
        for (let i = 0; i < 7; i++) {
            const date = new Date(currentWeekStart);
            date.setDate(currentWeekStart.getDate() + i);
            dates.push(date);
        }
        return dates;
    }, [currentWeekStart]);

    const fetchWeekAvailability = useCallback(async () => {
        if (!currentWeekStart || idsToQuery.length === 0) return;

        setLoading(true);
        setAvailabilityHint('');
        try {
            const weekDates = getWeekDates();
            const token = localStorage.getItem('token');
            if (!token && !isAdminMode) {
                setAvailability({});
                setAvailabilityHint('Sign in to load availability and select time slots.');
                return;
            }

            const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

            const availabilityPromises = weekDates.map(async (date) => {
                const dateStr = formatDateForAPI(date);

                if (isAdminMode) {
                    const bookingResponse = await fetch('/api/equipment-bookings/all', {
                        headers: authHeaders
                    });

                    if (bookingResponse.ok) {
                        const allBookings = await bookingResponse.json();
                        const relevantBookings = allBookings.filter((booking) => {
                            const bookingDate = formatDateForAPI(new Date(booking.eqm_booking_date));
                            const matchesEquipment = idsToQuery.some(
                                (eqId) =>
                                    String(booking.equipment_id) === eqId ||
                                    (booking.equipment_ids || []).some((x) => String(x) === eqId)
                            );
                            return (
                                bookingDate === dateStr &&
                                matchesEquipment &&
                                ['pending', 'confirmed'].includes(booking.status)
                            );
                        });
                        return {
                            date: dateStr,
                            slots: [],
                            bookings: relevantBookings,
                            anyOk: true
                        };
                    }
                    return {
                        date: dateStr,
                        slots: [],
                        bookings: [],
                        anyOk: false,
                        httpStatus: bookingResponse.status
                    };
                }

                if (resourceType === 'venue') {
                    const idEnc = encodeURIComponent(idsToQuery[0]);
                    const path = `/api/venue-bookings/availability/${idEnc}?date=${encodeURIComponent(dateStr)}`;
                    const response = await fetch(path, {
                        headers: authHeaders
                    });
                    if (response.ok) {
                        const data = await response.json();
                        return {
                            date: dateStr,
                            slots: data.availableSlots || [],
                            bookings: [],
                            anyOk: true
                        };
                    }
                    return { date: dateStr, slots: [], bookings: [], anyOk: false, httpStatus: response.status };
                }

                const union = new Set();
                let anyOk = false;
                let lastHttpStatus = null;
                for (const eqId of idsToQuery) {
                    const idEnc = encodeURIComponent(eqId);
                    const path = `/api/equipment-bookings/availability/${idEnc}?date=${encodeURIComponent(dateStr)}`;
                    const response = await fetch(path, {
                        headers: authHeaders
                    });
                    if (response.ok) {
                        anyOk = true;
                        const data = await response.json();
                        (data.availableSlots || []).forEach((s) => union.add(s));
                    } else {
                        lastHttpStatus = response.status;
                    }
                }
                const mergedSlots = TIME_SLOTS.filter((t) => union.has(t));
                return {
                    date: dateStr,
                    slots: mergedSlots,
                    bookings: [],
                    anyOk,
                    httpStatus: lastHttpStatus
                };
            });

            const results = await Promise.all(availabilityPromises);

            const availabilityMap = {};
            const bookingDetailsMap = {};
            let weekHadSuccess = false;

            results.forEach((row) => {
                const { date, slots, bookings, anyOk } = row;
                availabilityMap[date] = slots ?? [];
                if (anyOk) weekHadSuccess = true;
                if (isAdminMode && bookings) {
                    bookingDetailsMap[date] = bookings;
                }
            });

            setAvailability(availabilityMap);
            if (isAdminMode) {
                setBookingDetails(bookingDetailsMap);
            }

            if (!isAdminMode && !weekHadSuccess && idsToQuery.length > 0) {
                const hadUnauthorized = results.some((row) => row.httpStatus === 401);
                if (hadUnauthorized) {
                    localStorage.removeItem('token');
                    localStorage.removeItem('user');
                    window.location.href = '/login';
                    return;
                }
                setAvailabilityHint(
                    'Could not load availability (check network/login). Green slots appear when the server returns open hours.'
                );
            }
        } catch (error) {
            console.error('Error fetching availability:', error);
            setAvailabilityHint('Failed to load availability. Try refreshing the page.');
        } finally {
            setLoading(false);
        }
    }, [currentWeekStart, idsToQuery, isAdminMode, resourceType, getWeekDates]);

    useEffect(() => {
        if (!currentWeekStart || idsToQuery.length === 0) return;
        fetchWeekAvailability();
    }, [currentWeekStart, idsToQuery, fetchWeekAvailability]);

    const formatDateHeader = (date) => {
        const month = date.toLocaleDateString('en-US', { month: 'short' });
        const day = date.getDate();
        const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
        return { month, day, weekday };
    };

    const isWeekend = (date) => {
        const day = date.getDay();
        return day === 0 || day === 6;
    };

    const isDatePastOnly = (dateStr) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const d = new Date(`${dateStr}T12:00:00`);
        d.setHours(0, 0, 0, 0);
        return d < today;
    };

    const isSlotInPast = (dateStr, time) => {
        const slotStart = new Date(`${dateStr}T${time}:00`);
        return slotStart < new Date();
    };

    const isBeyondMaxBookable = (dateStr) => {
        if (!maxBookableDate) return false;
        const end = new Date(maxBookableDate);
        end.setHours(23, 59, 59, 999);
        const d = new Date(`${dateStr}T12:00:00`);
        return d > end;
    };

    const getAvailabilityCount = (date, time) => {
        const dateStr = formatDateForAPI(date);
        const slots = availability[dateStr];
        if (!slots) return 0;
        const isAvailable = slots.includes(time);
        return isAvailable ? totalUnits : 0;
    };

    const getBookingsForSlot = (date, time) => {
        if (!isAdminMode) return [];

        const dateStr = formatDateForAPI(date);
        const bookings = bookingDetails[dateStr] || [];

        return bookings.filter((booking) => {
            const bookingTime = booking.eqm_booking_time.substring(0, 5);
            const duration = booking.eqm_booking_duration || 1;
            const bookingStartHour = parseInt(bookingTime.split(':')[0], 10);
            const slotHour = parseInt(time.split(':')[0], 10);

            return slotHour >= bookingStartHour && slotHour < bookingStartHour + duration;
        });
    };

    const isSlotSelected = (dateStr, time) => {
        const key = slotKeyFromParts(dateStr, time);
        if (useMultiSelect) {
            return selectedSlotKeys.includes(key);
        }
        if (!selectedDate || !selectedTime) return false;
        return dateStr === selectedDate && time === selectedTime;
    };

    const canNavigateNext = () => {
        if (!currentWeekStart || !furthestWeekMonday) return true;
        const nextMonday = new Date(currentWeekStart);
        nextMonday.setDate(nextMonday.getDate() + 7);
        return nextMonday.getTime() <= furthestWeekMonday.getTime();
    };

    const handleSlotClick = (date, time) => {
        const dateStr = formatDateForAPI(date);
        const key = slotKeyFromParts(dateStr, time);

        if (isAdminMode) {
            const bookings = getBookingsForSlot(date, time);
            if (bookings.length > 0) {
                setSelectedSlotForDetails({ date: dateStr, time, bookings });
            }
            return;
        }

        if (blockWeekends && isWeekend(date)) {
            return;
        }

        if (isBeyondMaxBookable(dateStr)) {
            return;
        }

        if (isSlotInPast(dateStr, time)) {
            return;
        }

        const availableCount = getAvailabilityCount(date, time);
        if (availableCount === 0) {
            return;
        }

        if (useMultiSelect) {
            const next = selectedSlotKeys.includes(key)
                ? selectedSlotKeys.filter((k) => k !== key)
                : [...selectedSlotKeys, key].sort();
            onSelectedSlotKeysChange(next);
            return;
        }

        if (onSlotSelect) {
            onSlotSelect(dateStr, time);
        }
    };

    const navigateWeek = (direction) => {
        if (!currentWeekStart) return;
        if (direction > 0 && !canNavigateNext()) return;
        const newWeekStart = new Date(currentWeekStart);
        newWeekStart.setDate(currentWeekStart.getDate() + direction * 7);
        setCurrentWeekStart(newWeekStart);
    };

    const getSlotClassName = (date, time) => {
        const dateStr = formatDateForAPI(date);
        const key = slotKeyFromParts(dateStr, time);
        const selected = isSlotSelected(dateStr, time);
        const availableCount = getAvailabilityCount(date, time);
        const weekendDay = blockWeekends && isWeekend(date);
        const beyondMax = isBeyondMaxBookable(dateStr);
        const past = isSlotInPast(dateStr, time) || isDatePastOnly(dateStr);
        const blocked = weekendDay || beyondMax || past;
        const hovered = hoveredSlot === key;

        const slotH = compact ? 'min-h-[1.75rem] h-8' : 'h-16';
        let className = `${slotH} border-b border-r border-primary-200 dark:border-gray-600 transition-all duration-200 relative min-w-0 overflow-hidden`;

        if (blocked && !isAdminMode) {
            className += ' bg-gray-100 dark:bg-gray-800 cursor-not-allowed opacity-60';
        } else if (selected) {
            className += compact
                ? ' bg-emerald-700 dark:bg-emerald-600 ring-1 ring-emerald-400 cursor-pointer'
                : ' bg-emerald-700 dark:bg-emerald-600 ring-2 ring-emerald-500 cursor-pointer';
        } else if (weekendDay && isAdminMode) {
            className += ' bg-gray-100 dark:bg-gray-800 opacity-50';
        } else if (availableCount > 0) {
            className += ' cursor-pointer';
            const availabilityPercent = availableCount / totalUnits;
            if (availabilityPercent === 1) {
                className +=
                    ' bg-emerald-500 dark:bg-emerald-600 hover:bg-emerald-400 dark:hover:bg-emerald-500';
            } else if (availabilityPercent >= 0.5) {
                className +=
                    ' bg-emerald-400 dark:bg-emerald-500 hover:bg-emerald-300 dark:hover:bg-emerald-400';
            } else {
                className +=
                    ' bg-emerald-200 dark:bg-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-300';
            }
        } else {
            className +=
                ' bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-default';
        }

        if (hovered && !selected && !blocked && availableCount > 0) {
            className += compact ? ' ring-1 ring-primary-300' : ' ring-2 ring-primary-300 dark:ring-primary-600';
        }

        return className;
    };

    const getAvailabilityPercentage = () => {
        const weekDates = getWeekDates();
        let total = 0;
        let avail = 0;

        weekDates.forEach((date) => {
            if (blockWeekends && isWeekend(date)) return;
            const ds = formatDateForAPI(date);
            if (isBeyondMaxBookable(ds)) return;
            TIME_SLOTS.forEach((time) => {
                total++;
                if (getAvailabilityCount(date, time) > 0) avail++;
            });
        });

        return total > 0 ? (avail / total) * 100 : 0;
    };

    if (!currentWeekStart) {
        return <div className="text-center py-8 text-primary-500 dark:text-gray-400">Loading calendar...</div>;
    }

    const weekDates = getWeekDates();
    const availabilityPercent = getAvailabilityPercentage();

    return (
        <div className="space-y-4">
            {showStatsBar && (
                <Card>
                    <Card.Content>
                        <div className="text-center space-y-3">
                            <h3 className="text-lg font-serif font-semibold text-primary-900 dark:text-white">
                                {resolvedName}&apos;s Availability
                            </h3>
                            <div className="space-y-2">
                                <div className="flex justify-between text-sm text-primary-600 dark:text-gray-400">
                                    <span>0% Available</span>
                                    <span>100% Available</span>
                                </div>
                                <div className="relative h-8 bg-gradient-to-r from-white via-green-300 to-green-600 dark:from-gray-700 dark:via-green-500 dark:to-green-700 rounded-lg border border-primary-300 dark:border-gray-600">
                                    <div
                                        className="absolute inset-y-0 left-0 bg-primary-100 dark:bg-gray-800 opacity-50"
                                        style={{ width: `${100 - availabilityPercent}%` }}
                                    />
                                </div>
                                <div className="text-sm font-medium text-primary-700 dark:text-gray-300">
                                    {availabilityPercent.toFixed(0)}% of time slots available this week
                                </div>
                            </div>
                            <p className="text-sm text-primary-600 dark:text-gray-400 italic">
                                {isAdminMode
                                    ? 'Click on a time slot to see booking details'
                                    : 'Click green slots to select • Duration follows consecutive hours'}
                            </p>
                        </div>
                    </Card.Content>
                </Card>
            )}

            <div className="bg-white dark:bg-gray-800 rounded-lg border border-primary-200 dark:border-gray-600 overflow-hidden w-full max-w-full min-w-0 [contain:inline-size]">
                <div
                    className={`flex items-center justify-between bg-primary-50 dark:bg-gray-700 border-b border-primary-200 dark:border-gray-600 ${
                        compact ? 'p-2 sm:p-3' : 'p-4'
                    }`}
                >
                    <button
                        type="button"
                        onClick={() => navigateWeek(-1)}
                        className="p-2 hover:bg-primary-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
                        aria-label="Previous week"
                    >
                        <FiChevronLeft className="w-5 h-5 text-primary-700 dark:text-white" />
                    </button>

                    <div className="flex items-center gap-2">
                        <FiClock
                            className={`text-primary-600 dark:text-primary-400 ${compact ? 'w-4 h-4' : 'w-5 h-5'}`}
                        />
                        <h3
                            className={`font-serif font-semibold text-primary-900 dark:text-white ${compact ? 'text-sm sm:text-base' : ''}`}
                        >
                            Week of {formatDateHeader(weekDates[0]).month} {formatDateHeader(weekDates[0]).day}
                        </h3>
                    </div>

                    <button
                        type="button"
                        onClick={() => navigateWeek(1)}
                        disabled={!canNavigateNext()}
                        className="p-2 hover:bg-primary-100 dark:hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        aria-label="Next week"
                    >
                        <FiChevronRight className="w-5 h-5 text-primary-700 dark:text-white" />
                    </button>
                </div>

                {maxBookableDate && resourceType === 'equipment' && (
                    <p className="px-4 py-2 text-xs font-literary text-primary-600 dark:text-gray-400 border-b border-primary-100 dark:border-gray-600">
                        Equipment bookings are limited to 12 weeks from today. Later dates are not selectable.
                    </p>
                )}

                {availabilityHint && (
                    <p className="px-4 py-2 text-xs font-literary text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800">
                        {availabilityHint}
                    </p>
                )}

                <div className="w-full max-w-full min-w-0 overflow-x-hidden">
                    <div className="w-full min-w-0 max-w-full">
                        <div
                            className="grid w-full min-w-0 max-w-full bg-primary-100 dark:bg-gray-700"
                            style={weekGridTemplate}
                        >
                            <div
                                className={`border-r border-primary-200 dark:border-gray-600 min-w-0 ${
                                    compact ? 'p-0.5' : 'p-3'
                                }`}
                            />
                            {weekDates.map((date, index) => {
                                const { month, day, weekday } = formatDateHeader(date);
                                const weekendDay = blockWeekends && isWeekend(date);
                                return (
                                    <div
                                        key={index}
                                        className={`text-center border-r border-primary-200 dark:border-gray-600 min-w-0 max-w-full overflow-hidden ${
                                            compact ? 'px-0.5 py-0.5' : 'p-3'
                                        } ${weekendDay ? 'opacity-50' : ''}`}
                                    >
                                        <div
                                            className={`text-primary-500 dark:text-gray-400 truncate ${
                                                compact ? 'text-[8px] leading-tight' : 'text-xs'
                                            }`}
                                        >
                                            {month} {day}
                                        </div>
                                        <div
                                            className={`font-serif font-semibold text-primary-900 dark:text-white leading-none truncate ${
                                                compact ? 'text-[9px]' : ''
                                            }`}
                                        >
                                            {weekday}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {loading ? (
                            <div className="text-center py-8 text-primary-500 dark:text-gray-400">
                                Loading availability...
                            </div>
                        ) : (
                            TIME_SLOTS.map((time) => (
                                <div
                                    key={time}
                                    className="grid w-full min-w-0 max-w-full"
                                    style={weekGridTemplate}
                                >
                                    <div
                                        className={`font-medium text-primary-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 border-b border-r border-primary-200 dark:border-gray-600 flex items-center justify-center min-w-0 overflow-hidden ${
                                            compact
                                                ? 'px-0.5 py-0 text-[9px] leading-none tabular-nums'
                                                : 'p-3 text-sm'
                                        }`}
                                    >
                                        {time}
                                    </div>

                                    {weekDates.map((date) => {
                                        const dateStr = formatDateForAPI(date);
                                        const key = slotKeyFromParts(dateStr, time);
                                        const availableCount = getAvailabilityCount(date, time);
                                        const bookings = isAdminMode ? getBookingsForSlot(date, time) : [];

                                        return (
                                            <div
                                                key={key}
                                                role="button"
                                                tabIndex={0}
                                                className={getSlotClassName(date, time)}
                                                onClick={() => handleSlotClick(date, time)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        handleSlotClick(date, time);
                                                    }
                                                }}
                                                onMouseEnter={() => setHoveredSlot(key)}
                                                onMouseLeave={() => setHoveredSlot(null)}
                                            >
                                                {isSlotSelected(dateStr, time) && (
                                                    <div className="h-full flex items-center justify-center text-white text-[10px] sm:text-xs font-semibold">
                                                        {useMultiSelect || compact ? '✓' : 'Selected'}
                                                    </div>
                                                )}

                                                {isAdminMode && hoveredSlot === key && bookings.length > 0 && (
                                                    <div className="absolute inset-0 flex items-center justify-center">
                                                        <div className="bg-primary-900 dark:bg-primary-700 text-white text-xs p-2 rounded shadow-lg z-10">
                                                            <div className="font-semibold">{bookings.length} booking(s)</div>
                                                            <div className="text-[10px]">Click to view details</div>
                                                        </div>
                                                    </div>
                                                )}

                                                {!isAdminMode &&
                                                    !isWeekend(date) &&
                                                    availableCount > 0 &&
                                                    totalUnits > 1 &&
                                                    resourceType === 'equipment' && (
                                                        <div
                                                            className={`absolute text-white bg-primary-900/70 dark:bg-primary-800/70 rounded ${
                                                                compact
                                                                    ? 'bottom-0 right-0 text-[7px] px-0.5 leading-none'
                                                                    : 'bottom-1 right-1 text-[10px] px-1'
                                                            }`}
                                                        >
                                                            {availableCount}/{totalUnits}
                                                        </div>
                                                    )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {useMultiSelect && selectedSlotKeys.length > 0 && (
                    <div className="p-3 flex flex-wrap items-center justify-between gap-2 border-t border-primary-200 dark:border-gray-600 bg-primary-50/50 dark:bg-gray-700/50">
                        <div className="text-sm text-primary-700 dark:text-gray-300">
                            {selectedSlotKeys.length} slot{selectedSlotKeys.length !== 1 ? 's' : ''} selected
                        </div>
                        <button
                            type="button"
                            onClick={() => onSelectedSlotKeysChange([])}
                            className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                        >
                            Clear selection
                        </button>
                    </div>
                )}

                {!useMultiSelect && selectedDate && selectedTime && !isAdminMode && (
                    <div className="p-4 bg-primary-50 dark:bg-gray-700 border-t border-primary-200 dark:border-gray-600">
                        <div className="text-sm text-primary-700 dark:text-gray-300">
                            <span className="font-semibold">Selected:</span> {selectedDate} at {selectedTime}
                            {selectedDuration && ` for ${selectedDuration} hour(s)`}
                        </div>
                    </div>
                )}
            </div>

            {isAdminMode && selectedSlotForDetails && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <Card className="max-w-2xl w-full max-h-[80vh] overflow-auto">
                        <Card.Header>
                            <Card.Title className="flex items-center justify-between">
                                <span className="flex items-center gap-2">
                                    <FiUser />
                                    Bookings for {selectedSlotForDetails.date} at {selectedSlotForDetails.time}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setSelectedSlotForDetails(null)}
                                    className="text-primary-500 hover:text-primary-700 dark:text-gray-400 dark:hover:text-white"
                                >
                                    ×
                                </button>
                            </Card.Title>
                        </Card.Header>
                        <Card.Content>
                            <div className="space-y-4">
                                {selectedSlotForDetails.bookings.map((booking, index) => (
                                    <div
                                        key={index}
                                        className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-primary-200 dark:border-gray-600"
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <div>
                                                <div className="font-semibold text-primary-900 dark:text-white">
                                                    {booking.user?.First_Name} {booking.user?.Last_Name}
                                                </div>
                                                <div className="text-sm text-primary-600 dark:text-gray-400">
                                                    UID: {booking.user?.User_ID || booking.user_id}
                                                </div>
                                            </div>
                                            <Badge
                                                variant={
                                                    booking.status === 'confirmed'
                                                        ? 'success'
                                                        : booking.status === 'pending'
                                                          ? 'warning'
                                                          : 'neutral'
                                                }
                                            >
                                                {booking.status}
                                            </Badge>
                                        </div>

                                        <div className="grid grid-cols-2 gap-2 text-sm text-primary-700 dark:text-gray-300">
                                            <div>
                                                <strong>Time:</strong> {booking.eqm_booking_time}
                                            </div>
                                            <div>
                                                <strong>Duration:</strong> {booking.eqm_booking_duration}h
                                            </div>
                                            {booking.quantity && booking.quantity > 1 && (
                                                <div>
                                                    <strong>Quantity:</strong> {booking.quantity} units
                                                </div>
                                            )}
                                            {booking.affiliation && (
                                                <div className="col-span-2">
                                                    <strong>Affiliation:</strong> {booking.affiliation}
                                                </div>
                                            )}
                                            {booking.phone_number && (
                                                <div className="col-span-2">
                                                    <strong>Phone:</strong> {booking.phone_number}
                                                </div>
                                            )}
                                            {booking.remarks && (
                                                <div className="col-span-2">
                                                    <strong>Remarks:</strong> {booking.remarks}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card.Content>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default TimeSlotCalendar;
